<style>
.modal-container {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100%;
  height: 100%;
}

.modal-container .modal {
  z-index: 1055;
}

.modal-container .modal-backdrop {
  z-index: 1050;
}
</style>
<template>
  <BasePage :pageTitle="__DICT__.menuOptions.NightlifePlan" show-menu back-button show-footer>
    <div class="container-xl">
      <!-- Filters -->
      <Card margin="mb-4">
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label">{{ __DICT__.group }}</label>
            <Select v-model="selectedGroupId" :options="groupOptions" />
          </div>
          <div class="col-md-4">
            <label class="form-label">{{ __DICT__.day }}</label>
            <Select v-model="selectedDay" :options="dayOptions" />
          </div>
          <div class="col-md-4">
            <label class="form-label">{{ __DICT__.nightlife.eventType }}</label>
            <Select v-model="selectedSubtitle" :options="subtitleOptions" />
          </div>
        </div>
      </Card>

      <!-- Loading state -->
      <LoadingSpinner v-if="!nightlifeData" text="Loading events..." />

      <!-- No results state -->
      <div v-else-if="filteredDays.length === 0" class="text-center my-5">
        <div class="empty">
          <div class="empty-icon">
            <i class="ti ti-calendar-off" style="font-size: 3rem;"></i>
          </div>
          <p class="empty-title">{{ __DICT__.nightlife.noEventsFound }}</p>
          <p class="empty-subtitle text-muted">
            {{ __DICT__.nightlife.tryChangingFilters }}
          </p>
          <div class="empty-action">
            <button @click="resetFilters" class="btn btn-primary">
              {{ __DICT__.nightlife.resetFilters }}
            </button>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div v-else>
        <div v-for="day in filteredDays" :key="day.timestamp" class="mb-5">
          <div class="d-flex align-items-center mb-3">
            <div class="d-flex align-items-center">
              <i class="ti ti-calendar me-2 text-primary"></i>
              <h3 class="m-0">{{ formatDate(day.timestamp) }}</h3>
            </div>
            <div class="ms-auto badge bg-primary-lt">
              {{ day.events.length }} {{ day.events.length === 1 ? __DICT__.nightlife.event : __DICT__.nightlife.events }}
            </div>
          </div>

          <div class="row row-cards g-4">
            <div v-for="event in day.events" :key="event.id" class="col-md-6 col-lg-4">
              <div class="card card-sm h-100 shadow-sm">
                <div class="ribbon ribbon-top ribbon-start bg-primary" v-if="isPopularEvent(event)">
                  <IconStarFilled></IconStarFilled>
                </div>
                <div class="img-responsive img-responsive-21x9 card-img-top"
                     :style="`background-image: url(${event.image}); background-size: cover; background-position: center;`">
                </div>
                <div class="card-body">
                  <h3 class="card-title">{{ event.title }}</h3>
                  <p class="text-muted">{{ event.subtitle }}</p>

                  <div class="d-flex gap-2 mb-3 flex-wrap">
                    <span v-for="group in event.packages" :key="group.id" class="badge bg-blue-lt">
                      {{ group.name }}
                    </span>
                  </div>

                  <div class="d-flex gap-3 text-muted small mb-3">
                    <div>
                      <i class="ti ti-map-pin me-1"></i>
                      {{ event.location.name }}
                    </div>
                    <div>
                      <i class="ti ti-clock me-1"></i>
                      <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                      {{ formatTime(event.start) }} - {{ formatTime(event.end) }}
                    </div>
                  </div>

                  <p class="text-muted mb-3">
                    {{ truncateDescription(event.description) }}
                  </p>
                </div>
                <div class="card-footer">
                  <button class="btn btn-outline-primary w-100" @click="viewEventDetails(event, $event)">
                    {{ __DICT__.viewDetails }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Event Details Modal -->
    <!-- Event Details Modal -->
    <div v-if="selectedEvent">
      <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ selectedEvent?.title }}</h5>
              <button type="button" class="btn-close" @click="closeEventDetails"></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <img :src="selectedEvent?.image" class="w-100 rounded" style="max-height: 300px; object-fit: cover;"
                     :alt="selectedEvent?.title">
              </div>
              <div class="d-flex gap-3 text-muted mb-3">
                <div>
                  <i class="ti ti-map-pin me-1"></i>
                  {{ selectedEvent?.location.name }}
                </div>
                <div>
                  <i class="ti ti-clock me-1"></i>
                  <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                  {{ selectedEvent && formatTime(selectedEvent.start) }} -
                  {{ selectedEvent && formatTime(selectedEvent.end) }}
                </div>
              </div>
              <h6>{{ selectedEvent?.subtitle }}</h6>
              <div class="d-flex gap-2 mb-3 flex-wrap">
                <span v-for="group in selectedEvent?.packages" :key="group.id" class="badge bg-blue-lt">
                  {{ group.name }}
                </span>
              </div>
              <p>{{ selectedEvent?.description }}</p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-outline-secondary me-auto" @click="closeEventDetails">
                {{ __DICT__.close }}
              </button>
              <a :href="googleMapsUrl(selectedEvent?.location)" target="_blank" class="btn btn-primary">
                <i class="ti ti-map me-1"></i> {{ __DICT__.nightlife.viewOnMaps }}
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show"></div>
    </div>
  </BasePage>
</template>

<script setup>
import BasePage from "@/components/BasePage.vue";
import {ref, computed, onMounted} from "vue";
import {format} from "date-fns";
import {api} from "@/services/api";
import {useWeekStore} from "@/stores/week";
import {IconStarFilled} from "@tabler/icons-vue";
import { Card, LoadingSpinner, Select } from "@/components/atomic";

const nightlifeData = ref(null);
const weekStore = useWeekStore();
const selectedGroupId = ref("");
const selectedDay = ref("");
const selectedSubtitle = ref("");
const selectedEvent = ref(null);
const modalOpen = ref(false);

const formatDate = (timestamp) => {
  return new Date(timestamp).toLocaleString('en-GB', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC'
  });
};

const formatTime = (timestamp) => {
  return new Date(timestamp).toLocaleString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC'
  });
};
// Get Google Maps URL from location coordinates
const googleMapsUrl = (location) => {
  if (!location) return '#';
  return `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`;
};

// Truncate long descriptions
const truncateDescription = (description) => {
  return description.length > 100 ? `${description.substring(0, 100)}...` : description;
};

// Check if event is popular (for demonstration)
const isPopularEvent = (event) => {
  // This is just an example - you could base this on real metrics
  return event.packages.length > 1;
};

// Get days that have events
const daysWithEvents = computed(() => {
  if (!nightlifeData.value) return [];

  return nightlifeData.value.days.filter(day => day.events.length > 0);
});


const uniqueSubtitles = computed(() => {
  if (!nightlifeData.value) return [];

  const subtitles = new Set();

  nightlifeData.value.days.forEach(day => {
    day.events.forEach(event => {
      if (event.subtitle) {
        subtitles.add(event.subtitle);
      }
    });
  });
  console.log(subtitles)
  return Array.from(subtitles).sort();

})

// Get unique groups for the dropdown
const uniqueGroups = computed(() => {
  if (!nightlifeData.value) return [];

  const groupsMap = new Map();

  nightlifeData.value.days.forEach(day => {
    day.events.forEach(event => {
      event.packages.forEach(group => {
        groupsMap.set(group.id, group);
      });
    });
  });

  return Array.from(groupsMap.values())
      .sort((a, b) => a.name.localeCompare(b.name));
});

// Options for Select components
const groupOptions = computed(() => [
  { value: '', label: 'All Groups' },
  ...uniqueGroups.value.map(g => ({ value: g.id, label: g.name }))
]);

const dayOptions = computed(() => [
  { value: '', label: 'All Days' },
  ...daysWithEvents.value.map(d => ({ value: d.timestamp, label: formatDate(d.timestamp) }))
]);

const subtitleOptions = computed(() => [
  { value: '', label: 'All Types' },
  ...uniqueSubtitles.value.map(s => ({ value: s, label: s }))
]);

// Filter the days and events based on selected filters
const filteredDays = computed(() => {
  if (!nightlifeData.value) return [];

  let filteredData = [...nightlifeData.value.days];

  // Filter by selected day
  if (selectedDay.value) {
    filteredData = filteredData.filter(day => String(day.timestamp) === String(selectedDay.value));
  }

  // Filter events by selected group
  if (selectedGroupId.value) {
    filteredData = filteredData.map(day => ({
      ...day,
      events: day.events.filter(event =>
          event.packages.some(group => String(group.id) === String(selectedGroupId.value))
      )
    }));
  }

  if (selectedSubtitle.value) {
    filteredData = filteredData.map(day => ({
      ...day,
      events: day.events.filter(event => event.subtitle === selectedSubtitle.value)
    }));
  }

  // Remove days with no events
  return filteredData.filter(day => day.events.length > 0);
});

// Functions for event interactions
const viewEventDetails = (event, e) => {
  e.preventDefault();
  selectedEvent.value = event;
  document.body.classList.add('modal-open');
};

const closeEventDetails = () => {
  selectedEvent.value = null;
  document.body.classList.remove('modal-open');
};

const resetFilters = () => {
  selectedGroupId.value = "";
  selectedDay.value = "";
  selectedSubtitle.value = ""
};

onMounted(async () => {
  try {
    const response = await api.get(`/whatsOn/${weekStore.weekId}`);
    nightlifeData.value = await response;
  } catch (error) {
    console.error("Failed to fetch nightlife data:", error);
  }
});
</script>